<template>
  <div class="bg-light" style="min-height:100vh;">

    <!-- ── Top bar ─────────────────────────────────────────────── -->
    <div class="bg-white border-bottom shadow-sm px-3 py-2 d-flex justify-content-between align-items-center flex-wrap sticky-top" style="gap:.5rem;">
      <div class="d-flex align-items-center">
        <h5 class="mb-0 mr-2 text-dark font-weight-bold text-nowrap" style="font-size:1rem;">
          <i class="fas fa-fire mr-1 text-primary"></i>Kitchen Display
        </h5>
        <span v-if="activeCount" class="badge badge-primary badge-pill">{{ activeCount }}</span>
      </div>
      <div class="d-flex align-items-center flex-wrap" style="gap:.4rem;">
        <div class="custom-control custom-switch">
          <input type="checkbox" class="custom-control-input" id="kds-today" v-model="todayOnly" @change="fetchOrders">
          <label class="custom-control-label text-secondary" for="kds-today" style="font-size:.85rem;">Today only</label>
        </div>
        <div class="custom-control custom-switch">
          <input type="checkbox" class="custom-control-input" id="kds-hide-done" v-model="hideDone">
          <label class="custom-control-label text-secondary" for="kds-hide-done" style="font-size:.85rem;">Hide done</label>
        </div>
        <button
          class="btn btn-sm"
          :class="soundEnabled ? 'btn-outline-primary' : 'btn-outline-secondary'"
          @click="toggleSound"
          :title="soundEnabled ? 'Sound on — click to mute' : 'Sound off — click to enable'"
        >
          <i :class="soundEnabled ? 'fas fa-bell' : 'fas fa-bell-slash'"></i>
        </button>
        <button class="btn btn-sm btn-outline-secondary" @click="fetchOrders" :disabled="loading" title="Refresh">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
        </button>
      </div>
    </div>

    <!-- Browsers block sound until the page is tapped once -->
    <div v-if="soundEnabled && soundBlocked" class="alert alert-danger rounded-0 mb-0 d-flex justify-content-between align-items-center">
      <span><i class="fas fa-volume-mute mr-1"></i>Sound is blocked by the browser. Tap to turn on new-order alerts.</span>
      <button class="btn btn-danger" @click="unlockSound">Enable sound</button>
    </div>

    <!-- ── Station tabs ─────────────────────────────────────────── -->
    <div class="bg-white border-bottom px-3 py-2 d-flex flex-wrap">
      <button
        v-for="s in stationTabs"
        :key="s.key"
        class="btn btn-sm mr-1 mb-1"
        :class="activeStation === s.key ? 'btn-dark' : 'btn-outline-secondary'"
        @click="activeStation = s.key"
      >
        {{ s.label }}
        <span v-if="s.count" class="badge badge-secondary ml-1">{{ s.count }}</span>
      </button>
    </div>

    <!-- ── Loading ──────────────────────────────────────────────── -->
    <div v-if="loading && !orders.length" class="text-center py-5">
      <div class="spinner-border text-primary" role="status" style="width:3rem;height:3rem;"></div>
      <p class="text-secondary mt-3">Loading orders…</p>
    </div>

    <!-- ── Empty state ───────────────────────────────────────────── -->
    <div v-else-if="!normalizedOrders.length" class="text-center py-5">
      <i class="fas fa-check-circle fa-3x text-primary mb-3 d-block"></i>
      <h6 class="text-secondary">
        <span v-if="activeStation !== 'all'">No orders for {{ capitalize(activeStation) }}.</span>
        <span v-else>All clear — no pending orders.</span>
      </h6>
    </div>

    <!-- ── Orders grid ───────────────────────────────────────────── -->
    <div v-else class="kds-grid px-3 pt-3 pb-5">
      <div
        v-for="(kdsItem, index) in normalizedOrders"
        :key="kdsItem.id"
        class="card border-0 shadow-sm kds-card"
        :class="{
          'kds-card--new':     newOrderIds.includes(kdsItem.id),
          'kds-card--overdue': isOverdue(kdsItem),
        }"
      >
        <!-- Card header -->
        <div class="card-header px-3 py-2 border-0" :class="headerBg(kdsItem)">
          <div class="d-flex justify-content-between align-items-start" style="gap:.5rem;">

            <!-- Left: order info -->
            <div class="flex-grow-1" style="min-width:0;">
              <div class="d-flex align-items-center flex-wrap mb-1" style="gap:.25rem;">
                <span v-if="isOverdue(kdsItem)" class="badge badge-light">
                  <i class="fas fa-exclamation-triangle mr-1"></i>OVERDUE
                </span>
                <span v-else-if="kdsItem.priority === 'urgent'" class="badge badge-light">URGENT</span>
                <span v-if="kdsItem.delayed_flag" class="badge badge-light">DELAYED</span>
                <span class="badge" :class="statusBadgeClass(kdsItem.status)">{{ statusLabel(kdsItem.status) }}</span>
                <span class="font-weight-bold text-nowrap" style="font-size:.92rem;">
                  #{{ kdsItem.order ? kdsItem.order.order : '—' }}
                </span>
              </div>
              <div class="text-truncate" style="opacity:.88;font-size:.82rem;">
                <i class="fas fa-user-circle mr-1"></i>{{ sellerName(kdsItem) }}
              </div>
              <div class="mt-1 d-flex align-items-center flex-wrap" style="opacity:.75;gap:.25rem;font-size:.8rem;">
                <span><i class="fas fa-clock mr-1"></i>{{ fmtTime(kdsItem.sent_at || kdsItem.created_at) }}</span>
                <span class="badge badge-pill" :class="elapsedBadgeClass(kdsItem)">{{ elapsed(kdsItem) }}</span>
              </div>
            </div>

            <!-- Right: station + action -->
            <div class="text-right flex-shrink-0 d-flex flex-column align-items-end" style="gap:.35rem;">
              <span class="badge badge-secondary badge-pill" style="font-size:.62rem;letter-spacing:.5px;text-transform:uppercase;white-space:nowrap;">
                {{ capitalize(kdsItem.prep_station || 'General') }}
              </span>
              <button
                class="btn btn-sm btn-light text-nowrap"
                @click="handleAction(kdsItem, index)"
                :disabled="['done','cancelled'].includes(kdsItem.status) || !!updating[kdsItem.id]"
                style="font-size:.75rem;"
              >
                <span v-if="updating[kdsItem.id]" class="spinner-border spinner-border-sm mr-1" style="width:.65rem;height:.65rem;"></span>
                <i v-else :class="actionIcon(kdsItem)" class="mr-1"></i>
                {{ actionLabel(kdsItem) }}
              </button>
            </div>
          </div>

          <!-- SLA progress bar — shown for all active statuses -->
          <div v-if="kdsItem.sla_minutes && !['done','cancelled'].includes(kdsItem.status)" class="mt-2">
            <div class="d-flex justify-content-between mb-1" style="font-size:.62rem;opacity:.8;">
              <span>SLA {{ kdsItem.sla_minutes }}m</span>
              <span>{{ slaElapsedMin(kdsItem) }}m elapsed</span>
            </div>
            <div class="progress" style="height:3px;">
              <div class="progress-bar" :class="slaBarClass(kdsItem)" :style="{ width: slaPercent(kdsItem) + '%' }"></div>
            </div>
          </div>
        </div>

        <!-- Items list -->
        <ul class="list-group list-group-flush">
          <li
            v-for="item in kdsItem.normalizedItems"
            :key="item.id"
            class="list-group-item px-3 py-2"
            :class="[
              { 'kds-item--struck': ['done','served','cancelled'].includes(item.status) },
              itemRowClass(item),
            ]"
          >
            <!-- Simple item -->
            <template v-if="!item.isGrouped">
              <div class="d-flex justify-content-between align-items-center" style="gap:.5rem;">
                <div class="d-flex align-items-center flex-wrap flex-grow-1" style="min-width:0;gap:.25rem;">
                  <span class="font-weight-bold text-primary text-nowrap">{{ item.quantity }}×</span>
                  <span class="text-dark" style="font-size:.85rem;">{{ item.name }}</span>
                  <span class="kds-stock-dot flex-shrink-0" :class="'kds-stock-dot--' + item.stockStatus" :title="stockLabel(item.stockStatus)"></span>
                  <span v-if="item.notes" class="text-muted font-italic w-100" style="font-size:.78rem;">{{ item.notes }}</span>
                </div>
                <div class="flex-shrink-0">
                  <button
                    v-if="item.status === 'queued'"
                    class="btn btn-xs btn-outline-secondary"
                    @click="startItem(item, kdsItem, index)"
                    title="Start preparing"
                    aria-label="Start preparing"
                  ><i class="fas fa-play"></i></button>
                  <button
                    v-else-if="item.status === 'preparing'"
                    class="btn btn-xs btn-outline-primary"
                    @click="markItemReady(item, kdsItem, index)"
                    title="Mark ready"
                    aria-label="Mark ready"
                  ><i class="fas fa-check"></i></button>
                  <span v-else-if="item.status === 'ready'" class="badge badge-primary">Ready</span>
                  <span v-else-if="['done','served'].includes(item.status)" class="badge badge-secondary"><i class="fas fa-check"></i></span>
                  <span v-else-if="item.status === 'cancelled'" class="badge badge-secondary">void</span>
                </div>
              </div>
            </template>

            <!-- Grouped item -->
            <template v-else>
              <div class="d-flex justify-content-between align-items-center" style="gap:.5rem;">
                <div class="d-flex align-items-center flex-wrap flex-grow-1" style="min-width:0;gap:.25rem;">
                  <span class="font-weight-bold text-primary text-nowrap">{{ item.quantity }}×</span>
                  <span class="text-dark font-weight-bold" style="font-size:.85rem;">{{ item.name }}</span>
                  <span class="badge badge-secondary" style="font-size:.6rem;">bundle</span>
                  <span class="kds-stock-dot flex-shrink-0" :class="'kds-stock-dot--' + item.stockStatus" :title="stockLabel(item.stockStatus)"></span>
                  <span v-if="item.notes" class="text-muted font-italic w-100" style="font-size:.78rem;">{{ item.notes }}</span>
                </div>
                <div class="d-flex align-items-center flex-shrink-0" style="gap:.25rem;">
                  <button
                    v-if="item.status === 'queued'"
                    class="btn btn-xs btn-outline-secondary"
                    @click="startItem(item, kdsItem, index)"
                    title="Start preparing"
                    aria-label="Start preparing"
                  ><i class="fas fa-play"></i></button>
                  <button
                    v-else-if="item.status === 'preparing'"
                    class="btn btn-xs btn-outline-primary"
                    @click="markItemReady(item, kdsItem, index)"
                    title="Mark ready"
                    aria-label="Mark ready"
                  ><i class="fas fa-check"></i></button>
                  <span v-else-if="item.status === 'ready'" class="badge badge-primary">Ready</span>
                  <span v-else-if="['done','served'].includes(item.status)" class="badge badge-secondary"><i class="fas fa-check"></i></span>
                  <!-- Toggle expand -->
                  <button class="btn btn-xs btn-outline-secondary" @click="toggleExpand(item.id)" title="Show components" aria-label="Show components">
                    <i :class="expandedGrouped[item.id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                  </button>
                </div>
              </div>

              <!-- Expanded components list -->
              <div v-if="expandedGrouped[item.id]" class="mt-2 ml-3 kds-components">
                <div
                  v-for="comp in item.components"
                  :key="comp.id"
                  class="d-flex justify-content-between align-items-center py-1 kds-component-row"
                >
                  <div class="d-flex align-items-center">
                    <span class="kds-stock-dot mr-2" :class="'kds-stock-dot--' + comp.stockStatus"></span>
                    <span style="font-size:.82rem;">{{ comp.count }}× {{ comp.name }}</span>
                  </div>
                  <small class="text-muted ml-2">
                    <span :class="comp.stockStatus === 'out' ? 'text-danger font-weight-bold' : comp.stockStatus === 'low' ? 'text-warning font-weight-bold' : 'text-success'">
                      {{ comp.remaining_units !== null ? comp.remaining_units + ' left' : '—' }}
                    </span>
                  </small>
                </div>
              </div>
            </template>
          </li>
        </ul>

        <!-- Notes -->
        <div
          v-if="kdsItem.order && kdsItem.order.ordernotes && kdsItem.order.ordernotes.length"
          class="px-3 py-2 bg-light border-left border-primary text-dark kds-note-strip"
        >
          <i class="fas fa-sticky-note text-primary mr-1"></i>{{ kdsItem.order.ordernotes[0].note }}
        </div>
        <div v-if="kdsItem.notes" class="px-3 py-2 bg-light border-left border-secondary text-muted kds-note-strip">
          <i class="fas fa-info-circle mr-1"></i>{{ kdsItem.notes }}
        </div>

        <!-- Card footer -->
        <div class="card-footer bg-white border-0 py-2 px-3 d-flex justify-content-between align-items-center">
          <small class="text-muted">{{ kdsItem.items ? kdsItem.items.length : 0 }} item(s)</small>
          <small v-if="kdsItem.status === 'done'" class="text-primary font-weight-bold">
            <i class="fas fa-check-circle mr-1"></i>Done
          </small>
          <small v-else-if="kdsItem.status === 'cancelled'" class="text-muted">Cancelled</small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { getEcho } from '../../services/echo';

export default {
  name: 'KDSList',
  data() {
    return {
      orders:             [],
      previousIds:        [],
      loadedOnce:         false,
      soundBlocked:       false,
      newOrderIds:        [],
      todayOnly:          true,
      hideDone:           true,
      activeStation:      'all',
      loading:            false,
      updating:           {},
      expandedGrouped:    {},
      sound:              new Audio('/storage/Notifsounds/mixkit-positive-notification-951.wav'),
      soundEnabled:       true,
      timer:              null,
      clockTick:          0,
      clockTimer:         null,
      overdueAlertedIds:  [],
    };
  },

  computed: {
    stationTabs() {
      const counts = {};
      this.orders.forEach(o => {
        const s = o.prep_station || 'general';
        if (!['done', 'cancelled'].includes(o.status)) {
          counts[s] = (counts[s] || 0) + 1;
        }
      });
      const unique = [...new Set(this.orders.map(o => o.prep_station || 'general'))];
      return [
        { key: 'all', label: 'All Stations', count: Object.values(counts).reduce((a, b) => a + b, 0) },
        ...unique.map(s => ({ key: s, label: this.capitalize(s), count: counts[s] || 0 })),
      ];
    },

    activeCount() {
      return this.orders.filter(o => ['pending', 'in_progress'].includes(o.status)).length;
    },

    filteredOrders() {
      let list = this.orders;
      if (this.hideDone) {
        list = list.filter(o => !['done', 'cancelled'].includes(o.status));
      }
      if (this.todayOnly) {
        const today = new Date().toISOString().slice(0, 10);
        list = list.filter(o => (o.sent_at || o.created_at || '').startsWith(today));
      }
      if (this.activeStation !== 'all') {
        list = list.filter(o => (o.prep_station || 'general') === this.activeStation);
      }
      return list;
    },

    normalizedOrders() {
      const statusRank = o => {
        if (['done', 'cancelled'].includes(o.status)) return 4;
        if (o.status === 'in_progress') return 2;
        if (this.isOverdue(o)) return 0;
        return 1;
      };

      return this.filteredOrders
        .map(kdsOrder => {
          const productStockMap = {};
          if (kdsOrder.order && Array.isArray(kdsOrder.order.orders)) {
            kdsOrder.order.orders.forEach(o => {
              if (o.product_id && o.product) productStockMap[o.product_id] = o.product;
            });
          }

          const normalizedItems = (kdsOrder.items || []).map(item => {
            if (item.grouped_item) {
              const components = (item.grouped_item.grouped_items || []).map(gi => ({
                id:              gi.id,
                name:            gi.product ? gi.product.product : 'Unknown',
                count:           gi.count,
                remaining_units: gi.product != null ? gi.product.remaining_units : null,
                minimum_stock:   gi.product != null ? gi.product.minimum_stock   : null,
                stockStatus:     this.getProductStockStatus(gi.product),
              }));

              const statuses     = components.map(c => c.stockStatus);
              const overallStock = statuses.includes('out') ? 'out'
                                 : statuses.includes('low') ? 'low'
                                 : 'ok';

              return {
                id:          item.id,
                name:        item.grouped_item.group_title || item.name || 'Bundle',
                quantity:    item.quantity,
                notes:       item.notes,
                status:      item.status,
                isGrouped:   true,
                components,
                stockStatus: overallStock,
              };
            }

            const enrichedProduct = productStockMap[item.product_id] || item.product;
            return {
              id:          item.id,
              name:        item.name || (item.product ? item.product.product : 'Item'),
              quantity:    item.quantity,
              notes:       item.notes,
              status:      item.status,
              isGrouped:   false,
              product:     enrichedProduct,
              stockStatus: this.getProductStockStatus(enrichedProduct),
            };
          });

          return Object.assign({}, kdsOrder, { normalizedItems });
        })
        .sort((a, b) => {
          const ra = statusRank(a), rb = statusRank(b);
          if (ra !== rb) return ra - rb;
          return new Date(a.sent_at || a.created_at) - new Date(b.sent_at || b.created_at);
        });
    },
  },

  mounted() {
    this.fetchOrders();
    this.timer      = setInterval(this.fetchOrders, 15000);
    this.clockTimer = setInterval(() => {
      this.clockTick++;
      this.alertOverdue();
    }, 60000);

    this._onVisibility = () => {
      if (document.visibilityState === 'visible') this.fetchOrders();
    };
    document.addEventListener('visibilitychange', this._onVisibility);

    this.subscribeRealtime();

    // Before any tap, browsers refuse audible play — flag it up front so the
    // kitchen sees the banner instead of silently missing the first order.
    const ua = navigator.userActivation;
    this.soundBlocked = ua ? !ua.hasBeenActive : false;
    document.addEventListener('pointerdown', this.unlockSound);
  },

  beforeDestroy() {
    clearInterval(this.timer);
    clearInterval(this.clockTimer);
    document.removeEventListener('visibilitychange', this._onVisibility);
    if (this.kdsChannel) getEcho().leave(this.kdsChannel);
    document.removeEventListener('pointerdown', this.unlockSound);
  },

  methods: {
    // Instant refetch when an order is sent to the kitchen; the 15s poll
    // stays as the fallback if the socket is down.
    subscribeRealtime() {
      let companyId = null;
      try {
        const userDetails = JSON.parse(window.localStorage.getItem('userDetails'));
        companyId = userDetails && userDetails.company && userDetails.company.id;
      } catch (e) { /* no realtime, polling covers it */ }
      if (!companyId) return;

      this.kdsChannel = `company.${companyId}.kds`;
      getEcho().private(this.kdsChannel).listen('.kds.changed', () => this.fetchOrders());
    },

    async fetchOrders() {
      if (document.visibilityState !== 'visible') return;
      this.loading = true;
      try {
        const { data }    = await axios.get('/api/kds/orders');
        const incoming    = data.data || [];
        const incomingIds = incoming.map(o => o.id);
        const freshIds    = incomingIds.filter(id => !this.previousIds.includes(id));

        if (freshIds.length && this.loadedOnce) {
          this.newOrderIds.push(...freshIds);
          this.playSound();
          setTimeout(() => {
            this.newOrderIds = this.newOrderIds.filter(id => !freshIds.includes(id));
          }, 3000);
        }

        this.orders      = incoming;
        this.previousIds = incomingIds;
        this.loadedOnce  = true;
      } catch (err) {
        console.error('[KDS] fetch failed:', err);
      } finally {
        this.loading = false;
      }
    },

    handleAction(kdsItem, index) {
      if (kdsItem.status === 'pending')          this.updateStatus(kdsItem.id, 'acknowledge', index);
      else if (kdsItem.status === 'in_progress') this.updateStatus(kdsItem.id, 'done', index);
    },

    async updateStatus(orderId, action, index) {
      this.$set(this.updating, orderId, true);
      try {
        const url = action === 'acknowledge'
          ? `/api/kds/orders/${orderId}/ack`
          : `/api/kds/orders/${orderId}/complete`;

        const { data } = await axios.post(url);
        const updated  = data.data;
        const prev     = this.orders[index];
        this.$set(this.orders, index, {
          ...updated,
          order:  updated.order  || prev.order,
          sender: updated.sender || prev.sender,
        });
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        this.$toasts?.push({ type: 'error', message: `Order update failed: ${msg}` });
        console.error('[KDS] update failed:', err);
      } finally {
        this.$delete(this.updating, orderId);
      }
    },

    async startItem(item, kdsOrder, orderIndex) {
      try {
        const { data } = await axios.post('/api/kds/item/start', { item_id: item.id });
        this.patchItem(kdsOrder, orderIndex, data.data);
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        this.$toasts?.push({ type: 'error', message: `Failed to start item: ${msg}` });
        console.error('[KDS] startItem failed:', err);
      }
    },

    async markItemReady(item, kdsOrder, orderIndex) {
      try {
        const { data } = await axios.post('/api/kds/item/ready', { item_id: item.id });
        this.patchItem(kdsOrder, orderIndex, data.data);
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        this.$toasts?.push({ type: 'error', message: `Failed to mark item ready: ${msg}` });
        console.error('[KDS] markReady failed:', err);
      }
    },

    patchItem(kdsOrder, orderIndex, updatedItem) {
      const items = (kdsOrder.items || []).map(i =>
        i.id === updatedItem.id ? { ...i, ...updatedItem } : i
      );
      this.$set(this.orders, orderIndex, { ...kdsOrder, items });
    },

    toggleExpand(itemId) {
      this.$set(this.expandedGrouped, itemId, !this.expandedGrouped[itemId]);
    },

    toggleSound() {
      this.soundEnabled = !this.soundEnabled;
      if (this.soundEnabled) this.playSound();
    },

    playSound() {
      if (!this.soundEnabled) return;
      this.sound.currentTime = 0;
      this.sound.play()
        .then(() => { this.soundBlocked = false; })
        .catch(() => { this.soundBlocked = true; });
    },

    // Any tap on the page counts as the user gesture browsers need for audio.
    unlockSound() {
      this.sound.muted = true;
      this.sound.play()
        .then(() => {
          this.sound.pause();
          this.sound.currentTime = 0;
          this.sound.muted = false;
          this.soundBlocked = false;
          document.removeEventListener('pointerdown', this.unlockSound);
        })
        .catch(() => { this.sound.muted = false; this.soundBlocked = true; });
    },

    alertOverdue() {
      const activeIds = this.orders.map(o => o.id);
      this.overdueAlertedIds = this.overdueAlertedIds.filter(id => activeIds.includes(id));
      let hasNew = false;
      this.orders.forEach(o => {
        if (!this.isOverdue(o)) return;
        if (!this.overdueAlertedIds.includes(o.id)) {
          this.overdueAlertedIds.push(o.id);
          hasNew = true;
        }
      });
      if (hasNew) this.playSound();
    },

    elapsedMins(kdsItem) {
      void this.clockTick;
      return Math.floor((Date.now() - new Date(kdsItem.sent_at || kdsItem.created_at)) / 60000);
    },

    isOverdue(kdsItem) {
      if (kdsItem.status !== 'pending') return false;
      return this.elapsedMins(kdsItem) >= 10;
    },

    getProductStockStatus(product) {
      if (!product) return 'ok';
      const remaining = product.remaining_units;
      const minimum   = product.minimum_stock;
      if (remaining == null) return 'ok';
      if (remaining <= 0)    return 'out';
      if (minimum != null && remaining <= minimum) return 'low';
      return 'ok';
    },

    stockLabel(status) {
      if (status === 'out') return 'Out of stock';
      if (status === 'low') return 'Low stock';
      return 'In stock';
    },

    statusLabel(status) {
      const map = {
        pending:     'Pending',
        in_progress: 'In Progress',
        done:        'Done',
        cancelled:   'Cancelled',
        served:      'Served',
      };
      return map[status] || status;
    },

    itemRowClass(item) {
      if (item.stockStatus === 'out') return 'kds-item--out';
      if (item.stockStatus === 'low') return 'kds-item--low';
      return '';
    },

    statusBadgeClass(status) {
      if (status === 'in_progress') return 'badge-warning text-dark';
      if (status === 'pending')     return 'badge-secondary';
      if (status === 'done')        return 'badge-success';
      if (status === 'cancelled')   return 'badge-danger';
      return 'badge-secondary';
    },

    sellerName(kdsItem) {
      const s = kdsItem.order && kdsItem.order.soldby;
      if (s) return s.full_name || s.name;
      return kdsItem.sender ? kdsItem.sender.name : 'Unknown';
    },

    capitalize(str) {
      return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
    },

    fmtTime(dt) {
      if (!dt) return '';
      return new Date(dt).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });
    },

    elapsed(kdsItem) {
      const mins = this.elapsedMins(kdsItem);
      return mins < 1 ? 'just now' : `${mins}m`;
    },

    elapsedBadgeClass(kdsItem) {
      const mins  = this.elapsedMins(kdsItem);
      const limit = kdsItem.sla_minutes || 10;
      if (mins >= limit)       return 'badge-danger';
      if (mins >= limit * 0.7) return 'badge-warning text-dark';
      return 'badge-secondary';
    },

    slaElapsedMin(kdsItem) {
      void this.clockTick;
      const start = kdsItem.received_at || kdsItem.sent_at || kdsItem.created_at;
      if (!start) return 0;
      return Math.floor((Date.now() - new Date(start)) / 60000);
    },

    slaPercent(kdsItem) {
      return Math.min(100, Math.round((this.slaElapsedMin(kdsItem) / kdsItem.sla_minutes) * 100));
    },

    slaBarClass(kdsItem) {
      const p = this.slaPercent(kdsItem);
      if (p >= 100) return 'bg-danger';
      if (p >= 70)  return 'bg-warning';
      return 'bg-success';
    },

    headerBg(kdsItem) {
      if (kdsItem.status === 'done')        return 'bg-secondary text-white';
      if (kdsItem.status === 'cancelled')   return 'bg-secondary text-white';
      if (kdsItem.status === 'in_progress') return 'bg-primary text-white';
      if (this.isOverdue(kdsItem))          return 'bg-danger text-white';
      if (kdsItem.priority === 'urgent')    return 'bg-danger text-white';
      if (kdsItem.delayed_flag)             return 'bg-dark text-white';
      return 'bg-dark text-white';
    },

    actionLabel(kdsItem) {
      if (kdsItem.status === 'done')        return 'Done';
      if (kdsItem.status === 'cancelled')   return 'Cancelled';
      if (kdsItem.status === 'in_progress') return 'Mark Done';
      return 'Acknowledge';
    },

    actionIcon(kdsItem) {
      if (kdsItem.status === 'done')        return 'fas fa-check-circle';
      if (kdsItem.status === 'in_progress') return 'fas fa-check';
      return 'fas fa-bell';
    },
  },
};
</script>

<style scoped>
.kds-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: 1rem;
}

@media (max-width: 575.98px) {
  .kds-grid {
    grid-template-columns: 1fr;
    padding-left: .75rem !important;
    padding-right: .75rem !important;
  }
}

.kds-card { transition: transform .2s; }
.kds-card:hover { transform: translateY(-2px); }

.kds-card--new { animation: pulse-new 1.5s ease-out; }
@keyframes pulse-new {
  0%   { box-shadow: 0 0 0 0 rgba(0,123,255,.5); }
  70%  { box-shadow: 0 0 0 10px rgba(0,123,255,0); }
  100% { box-shadow: none; }
}

.kds-card--overdue { animation: pulse-overdue 2s ease-in-out infinite; }
@keyframes pulse-overdue {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220,53,69,0); }
  50%       { box-shadow: 0 0 0 4px rgba(220,53,69,.4); }
}

.kds-item--struck { opacity: .45; text-decoration: line-through; }

.kds-item--out { background-color: rgba(220,53,69,.08); }
.kds-item--low { background-color: rgba(255,193,7,.1); }

.kds-stock-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.kds-stock-dot--ok  { background-color: #28a745; }
.kds-stock-dot--low { background-color: #fd7e14; }
.kds-stock-dot--out { background-color: #dc3545; }

.kds-components {
  border-left: 2px solid #dee2e6;
  padding-left: .5rem;
}
.kds-component-row { border-bottom: 1px dashed #f0f0f0; }
.kds-component-row:last-child { border-bottom: none; }

.kds-note-strip { border-left-width: 3px !important; }

.btn-xs {
  padding: .1rem .38rem;
  font-size: .68rem;
  line-height: 1.4;
  border-radius: 3px;
}
</style>
